import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  Fade,
  Slide,
  Collapse,
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset, CheckCircle } from '@mui/icons-material';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

const PasswordChange = () => {
  // Session validation
  useSessionValidation();
  
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { isAuthenticated, userId, logout, token } = useAuth();

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
    }
  }, [isAuthenticated]);

  const validatePassword = () => {
    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters long.');
      return false;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setErrorMessage('Password must contain at least one uppercase letter.');
      return false;
    }
    if (!/[a-z]/.test(newPassword)) {
      setErrorMessage('Password must contain at least one lowercase letter.');
      return false;
    }
    if (!/\d/.test(newPassword)) {
      setErrorMessage('Password must contain at least one digit.');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleChangePassword = async () => {
    if (!validatePassword()) return;

    setLoading(true);

    const payload = {
      CurrentPassword: oldPassword,
      NewPassword: newPassword,
      ConfirmPassword: confirmPassword,
      UserId: userId,
    };

    try {
      const response = await updateData('UserTokenApi', `${userId}`, payload, token);
      setSuccessMessage(response.message || 'Password changed successfully.');
      setErrorMessage('');
    } catch (error) {
      setSuccessMessage('');
      setErrorMessage('Failed to change password. Please try again.');
      console.error('Password change error:', error);
    } finally {
      setLoading(false);
    }
  };

  const passwordFieldProps = {
    type: showPassword ? 'text' : 'password',
    InputProps: {
      endAdornment: (
        <InputAdornment position="end">
          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    },
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#fafafa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        @keyframes checkmark {
          0% { transform: scale(0) rotate(0deg); }
          50% { transform: scale(1.2) rotate(180deg); }
          100% { transform: scale(1) rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(102, 187, 106, 0.3), 0 0 40px rgba(76, 175, 80, 0.2); }
          50% { box-shadow: 0 0 30px rgba(102, 187, 106, 0.5), 0 0 60px rgba(76, 175, 80, 0.3); }
        }
      `}</style>

      <Fade in timeout={800}>
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 100px rgba(102, 187, 106, 0.2)',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 1,
            maxWidth: '440px',
            width: '90vw',
            maxHeight: '90vh',
            animation: 'slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), glow 3s ease-in-out infinite',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #66bb6a, #81c784, #a5d6a7, #66bb6a)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 3s linear infinite',
            },
          }}
        >
          {/* Header with Icon */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)',
              p: 1.5,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '200%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                animation: 'shimmer 4s infinite',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
              },
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                mb: 0.5,
                transition: 'all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  transform: 'rotate(360deg) scale(1.1)',
                  backgroundColor: 'rgba(255, 255, 255, 0.35)',
                },
              }}
            >
              <LockReset sx={{ fontSize: 24, color: '#fff' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#fff',
                letterSpacing: '0.5px',
                fontSize: '1.1rem',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
              }}
            >
              Change Password
            </Typography>
          </Box>

          <Box sx={{ p: 2, maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}>
            {/* Alerts */}
            <Collapse in={!!successMessage || !!errorMessage}>
              <Box sx={{ mb: 1.5 }}>
                {successMessage && (
                  <Slide direction="down" in={!!successMessage}>
                    <Alert
                      severity="success"
                      icon={<CheckCircle />}
                      sx={{
                        borderRadius: 2.5,
                        fontWeight: 600,
                        py: 1,
                        background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
                        border: '1px solid #28a745',
                        boxShadow: '0 4px 12px rgba(40, 167, 69, 0.2)',
                      }}
                    >
                      {successMessage}
                    </Alert>
                  </Slide>
                )}
                {errorMessage && (
                  <Slide direction="down" in={!!errorMessage}>
                    <Alert
                      severity="error"
                      sx={{
                        borderRadius: 2.5,
                        fontWeight: 600,
                        py: 1,
                        background: 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)',
                        border: '1px solid #dc3545',
                        boxShadow: '0 4px 12px rgba(220, 53, 69, 0.2)',
                      }}
                    >
                      {errorMessage}
                    </Alert>
                  </Slide>
                )}
              </Box>
            </Collapse>

            {/* Password Fields */}
            <TextField
              fullWidth
              label="Current Password"
              variant="outlined"
              size="medium"
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  backgroundColor: '#f8f9fa',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: '#fff',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(102, 187, 106, 0.15)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#66bb6a',
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#fff',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(102, 187, 106, 0.25)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#66bb6a',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#66bb6a',
                  fontWeight: 600,
                },
              }}
            />

            <TextField
              fullWidth
              label="New Password"
              variant="outlined"
              size="medium"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              {...passwordFieldProps}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  backgroundColor: '#f8f9fa',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: '#fff',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(102, 187, 106, 0.15)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#66bb6a',
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#fff',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(102, 187, 106, 0.25)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#66bb6a',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#66bb6a',
                  fontWeight: 600,
                },
              }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              variant="outlined"
              size="medium"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              {...passwordFieldProps}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  backgroundColor: '#f8f9fa',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: '#fff',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(102, 187, 106, 0.15)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#66bb6a',
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#fff',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(102, 187, 106, 0.25)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#66bb6a',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#66bb6a',
                  fontWeight: 600,
                },
              }}
            />

            {/* Password Requirements - Compact */}
            <Collapse in={newPassword.length > 0}>
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  borderRadius: 2.5,
                  border: '1px solid #dee2e6',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.3s ease',
                }}
              >
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                {[
                  { test: newPassword.length >= 8, label: '8+ chars' },
                  { test: /[A-Z]/.test(newPassword), label: 'Uppercase' },
                  { test: /[a-z]/.test(newPassword), label: 'Lowercase' },
                  { test: /\d/.test(newPassword), label: 'Number' },
                ].map((req, idx) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    sx={{
                      color: req.test ? '#28a745' : '#6c757d',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontWeight: 500,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: req.test ? '#28a745' : '#dee2e6',
                        color: '#fff',
                        fontSize: 10,
                        fontWeight: 700,
                        animation: req.test ? 'checkmark 0.4s ease' : 'none',
                      }}
                    >
                      {req.test ? '✓' : ''}
                    </Box>
                    {req.label}
                  </Typography>
                ))}
              </Box>
              {confirmPassword && (
                <Typography
                  variant="caption"
                  sx={{
                    color: newPassword === confirmPassword ? '#28a745' : '#dc3545',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontWeight: 500,
                    mt: 0.5,
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      backgroundColor: newPassword === confirmPassword ? '#28a745' : '#dc3545',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 700,
                      animation: newPassword === confirmPassword ? 'checkmark 0.4s ease' : 'none',
                    }}
                  >
                    {newPassword === confirmPassword ? '✓' : '✕'}
                  </Box>
                  Passwords {newPassword === confirmPassword ? 'match' : 'do not match'}
                </Typography>
              )}
            </Box>
          </Collapse>

          {/* Update Button */}
          <Button
            variant="contained"
            fullWidth
            sx={{
              py: 1.2,
              fontWeight: 600,
              fontSize: '15px',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)',
              boxShadow: '0 4px 15px rgba(102, 187, 106, 0.4)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              textTransform: 'none',
              '&:hover': {
                transform: !loading ? 'translateY(-2px)' : 'none',
                boxShadow: !loading ? '0 6px 20px rgba(102, 187, 106, 0.6)' : 'none',
                background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
              },
              '&:active': {
                transform: !loading ? 'scale(0.98)' : 'none',
              },
              '&.Mui-disabled': {
                background: '#e9ecef',
                color: '#6c757d',
              },
            }}
            disabled={loading}
            onClick={handleChangePassword}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <>
                <LockReset sx={{ mr: 1, fontSize: 20 }} />
                Update Password
              </>
            )}
          </Button>

          {/* Footer */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: '#6c757d',
              mt: 1.5,
              fontWeight: 500,
              letterSpacing: '0.5px',
              fontSize: '0.7rem',
            }}
          >
            © EFFTABS SOLUTIONS LLP
          </Typography>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};

export default PasswordChange;
